import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, formatApiErrorDetail } from "@/components/portal/PortalAuth";
import { NewOrderModal } from "@/components/portal/QuickActions";
import { EmptyState, Spinner, StatusBadge, fmtDay, gbp2, inputCls, simpleOrderStatus } from "@/components/portal/shared";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function OrdersPage() {
  const { api } = usePortalAuth();
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));

  const deleteOrder = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/orders/${deleting.id}`);
      toast.success(`Order ${deleting.ref} deleted`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (orders === null) return <Spinner />;

  const rows = orders.filter((o) =>
    `${o.ref} ${o.customer} ${o.postcode || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="orders-page" className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order ref, customer, postcode…" className={`${inputCls} pl-10 rounded-full`} />
        </div>
        <button type="button" data-testid="orders-new" onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-navy-800 sm:ml-auto">
          <Plus className="h-3.5 w-3.5" /> New Order
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={Package}>No orders yet — orders appear when a quote is accepted, or tap New Order.</EmptyState>
      ) : (
        <div className="rounded-2xl border border-warm-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-warm-200 bg-warm-100/60">
                <th className="px-5 py-3">Order</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Install</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Balance due</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-warm-200">
              {rows.map((o) => {
                const paid = (o.deposit_paid || 0) + (o.balance_paid || 0);
                return (
                  <tr key={o.id} className="hover:bg-warm-100/50 transition-colors" data-testid={`order-row-${o.id}`}>
                    <td className="px-5 py-3.5">
                      <Link to={`/portal/orders/${o.id}`} data-testid={`order-open-${o.id}`} className="font-semibold text-navy-900 hover:underline">{o.ref}</Link>
                    </td>
                    <td className="px-4 py-3.5 text-slate-800">{o.customer}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-600">{o.install_date ? `${fmtDay(o.install_date)}${o.install_time ? ` ${o.install_time}` : ""}` : "Not booked"}</td>
                    <td className="px-4 py-3.5 font-semibold">{gbp2(o.total)}</td>
                    <td className={`px-4 py-3.5 font-semibold ${o.total - paid > 0 ? "text-amber-700" : "text-emerald-700"}`}>{gbp2(Math.max(0, (o.total || 0) - paid))}</td>
                    <td className="px-4 py-3.5"><StatusBadge s={simpleOrderStatus(o.status)} /></td>
                    <td className="px-4 py-3.5 text-right">
                      <button type="button" aria-label={`Delete order ${o.ref}`} data-testid={`order-delete-${o.id}`} onClick={() => setDeleting(o)} className="text-slate-300 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <NewOrderModal onClose={() => setShowNew(false)} />}
      <AlertDialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order {deleting?.ref}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order and any linked invoices. The customer record and their measurements stay on file. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orders-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="orders-delete-confirm" onClick={deleteOrder} disabled={busy} className="bg-red-700 hover:bg-red-800">Delete order</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
